import { Dispatch, Fragment, SetStateAction } from 'react';
import { useHistory } from 'react-router-dom';
import { openModal } from 'actions/modal';
import { format } from 'date-fns';
import { useThunkDispatch } from 'utils/reduxHook';
import Typography from 'components/atoms/Typography';
import Button from 'components/atoms/Button';
import { ConversationPrompts } from 'utils/gloo-types';
import Icon from 'components/atoms/Icon';
import Separator from 'components/atoms/Separator';
import CloudinaryVideoPlayer from 'components/organisms/CloudinaryVideoPlayer';

interface ConversationPromptDetails {
  prompt: ConversationPrompts;
  updatePromptInList: (prompt: ConversationPrompts) => void;
  isInMobileDetailsView: boolean;
  setSelectedPrompt: Dispatch<SetStateAction<ConversationPrompts>>;
}

const ConversationPromptsDetails = ({
  prompt,
  updatePromptInList,
  isInMobileDetailsView,
  setSelectedPrompt,
}: ConversationPromptDetails) => {
  const dispatch = useThunkDispatch();
  const history = useHistory();

  return (
    <>
      <div className="flex-1 border-l border-glooNavy-100">
        <div className="space-y-3 p-5">
          {isInMobileDetailsView && (
            <Button
              type="transparent"
              icon="arrow-left"
              onClick={() => setSelectedPrompt(null)}
              text="Back to All Prompts"
              dataDurableId="backToAllPromptsButton"
            />
          )}
          <div className="md:flex justify-between items-center">
            <Typography variant="h2" className="text-neutral-700">
              {prompt.name}
            </Typography>
          </div>
          <div className="flex text-neutral-700">
            <Icon
              name={`${
                prompt.status === 'Published'
                  ? 'eye'
                  : prompt.status === 'Unpublished'
                    ? 'eye-off'
                    : prompt.status === 'Draft'
                      ? 'file-text'
                      : undefined
              }`}
              size={12}
              className="mr-1"
            />
            <Typography variant="xs" className="mr-4">
              {prompt.status}
            </Typography>
            <Icon name="clock" size={12} className="mr-1" />
            <Typography variant="xs" className="mr-4">
              {format(new Date(prompt.updatedAt), 'MM/dd/yy h:mm a')}
            </Typography>
          </div>
          {prompt.status !== 'Archived' && (
            <div className="flex flex-row w-1/2 mb-6 space-x-2">
              <Button
                type="tertiary"
                size="small"
                dataDurableId="editConversationPromptButton"
                className="text-sm w-full justify-center"
                text="Edit"
                onClick={() => history.replace(`${window.location.pathname}/edit/${prompt.id}`)}
              />
              <Button
                type="tertiary"
                size="small"
                dataDurableId="unpublishConversationPromptButton"
                className="text-sm w-full justify-center"
                text={prompt.status === 'Published' ? 'Unpublish' : 'Publish'}
                onClick={() =>
                  dispatch(
                    openModal('ChangeConversationPromptStatus', {
                      prompt,
                      type: 'publish_unpublish',
                      updatePromptInList,
                    }),
                  )
                }
              />
            </div>
          )}
        </div>
        <Separator color="glooNavyLight" />
        <div className="flex flex-col w-full text-blackDark text-3xl font-medium md:h-[calc(100vh_-_400px)] md:max-h-[calc(100vh_-_400px)] overflow-auto inbox-scrollbar-y">
          <div className="mt-4 px-5 space-y-5">
            <div>
              <Typography variant="label" className="text-glooBlack-100 mb-2">
                Message Author
              </Typography>
              <div className="flex items-center">
                {prompt.authorImageUrl && (
                  <img src={prompt.authorImageUrl} alt={prompt.author} className="bg-white w-5 h-5 rounded-full mr-2" />
                )}
                <Typography variant="sm" className="text-neutral-700">
                  {prompt.author}
                </Typography>
              </div>
            </div>
            <div>
              <Typography variant="label" className="text-glooBlack-100 mb-2">
                Message Content
              </Typography>
              <Typography variant="sm" className="text-neutral-700">
                {prompt.message}
              </Typography>
            </div>
            <div>
              <Typography variant="label" className="text-glooBlack-100 mb-2">
                Message Tip
              </Typography>
              <Typography variant="sm" className="text-neutral-700">
                {prompt.promptTip}
              </Typography>
            </div>
            {prompt.videoUrl && (
              <div className="my-2 flex flex-col items-start">
                <Typography variant="label" className="text-glooBlack-100 mb-2">
                  Video (this gets attached as a shortened URL when sending out the message)
                </Typography>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <CloudinaryVideoPlayer url={prompt.videoUrl} wrapClassName="max-h-48" />
                </div>
              </div>
            )}
            <div>
              <Typography variant="label" className="text-glooBlack-100 mb-2">
                Topics
              </Typography>
              <Typography variant="sm" className="text-neutral-700">
                {prompt.topicsArr?.map((topic, index) => (
                  <Fragment key={topic.id}>{`${topic.title}${
                    prompt.topicsArr.length > index + 1 ? ',' : ''
                  } `}</Fragment>
                ))}
              </Typography>
            </div>
            {prompt.createdByOrgUser?.name && (
              <div>
                <Typography variant="label" className="text-glooBlack-100 mb-2">
                  Prompt Creator in System
                </Typography>
                <Typography variant="sm" className="text-neutral-700">
                  {prompt.createdByOrgUser.name}
                </Typography>
              </div>
            )}
            <Separator />
            <div className="text-left pb-6">
              {prompt.status !== 'Archived' && (
                <Button
                  type="destruction"
                  size="small"
                  dataDurableId="archiveConversationPromptButton"
                  className="text-xs justify-center"
                  text="Archive Prompt"
                  onClick={() =>
                    dispatch(
                      openModal('ChangeConversationPromptStatus', { prompt, type: 'archive', updatePromptInList }),
                    )
                  }
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConversationPromptsDetails;
